import React, { useId, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import SimpleFadeIn from './SimpleFadeIn';
import { Eyebrow } from './bandedStyles';

const FAQSection = styled.section<{ $embedded?: boolean; $split?: boolean }>`
  background: ${({ $embedded }) => $embedded ? 'transparent' : 'var(--color-primary-base)'};
  padding: ${({ $embedded }) => $embedded ? '0' : 'var(--spacing-section-mobile) 0'};
  /* The split layout sits in a page that spaces its own blocks. */
  margin-bottom: ${({ $embedded, $split }) => ($embedded && !$split ? 'var(--spacing-4xl)' : '0')};
  ${({ $embedded }) => $embedded ? 'position: relative; z-index: 2;' : ''}
  content-visibility: auto;
  contain-intrinsic-size: auto 600px;

  @media (min-width: 768px) {
    padding: ${({ $embedded }) => $embedded ? '0' : 'var(--spacing-section) 0'};
  }
`;

const FAQContainer = styled.div<{ $split?: boolean }>`
  max-width: ${({ $split }) => ($split ? 'none' : '800px')};
  margin: 0 auto;
  margin-top: ${({ $split }) => ($split ? '0' : 'var(--spacing-2xl)')};
`;

const FAQItem = styled.div<{ $split?: boolean }>`
  background: var(--white);
  border-radius: ${({ $split }) => ($split ? '12px' : '8px')};
  margin-bottom: ${({ $split }) => ($split ? '8px' : 'var(--spacing-md)')};
  overflow: hidden;
  box-shadow: ${({ $split }) => ($split ? 'none' : 'var(--shadow-sm)')};
  ${({ $split }) => $split && 'border: 1px solid var(--davinci-cloud);'}
  transition: box-shadow 0.3s ease;
  
  &:hover {
    box-shadow: ${({ $split }) => ($split ? '0 6px 20px rgba(20, 53, 98, 0.08)' : 'var(--shadow-md)')};
  }
`;

const FAQQuestion = styled.button<{ $isOpen: boolean; $split?: boolean }>`
  width: 100%;
  padding: ${({ $split }) => ($split ? '13px 16px' : 'var(--spacing-lg)')};
  background: none;
  border: none;
  text-align: left;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--spacing-md);
  transition: background-color 0.2s ease;
  
  &:hover {
    background-color: var(--color-primary-light);
  }
  
  ${({ $split }) => $split && `
    &:focus-visible {
      outline: 3px solid var(--davinci-blue-fill);
      outline-offset: -3px;
    }
  `}

  h3 {
    font-size: ${({ $split }) => ($split ? 'var(--fs-base)' : 'var(--fs-lg)')};
    color: ${({ $split }) => ($split ? 'var(--davinci-midnight)' : 'var(--text-primary)')};
    font-weight: var(--font-weight-bold);
    line-height: ${({ $split }) => ($split ? '1.3' : 'inherit')};
    margin: 0;
    flex: 1;
  }
  
  svg {
    width: ${({ $split }) => ($split ? '20px' : '24px')};
    height: ${({ $split }) => ($split ? '20px' : '24px')};
    color: ${({ $split }) => ($split ? 'var(--davinci-denim)' : 'var(--color-accent-primary)')};
    transform: ${({ $isOpen }) => $isOpen ? 'rotate(180deg)' : 'rotate(0)'};
    transition: transform 0.3s ease;
    flex-shrink: 0;
  }
`;

const FAQAnswer = styled.div<{ $isOpen: boolean; $split?: boolean }>`
  display: grid;
  grid-template-rows: ${({ $isOpen }) => $isOpen ? '1fr' : '0fr'};
  opacity: ${({ $isOpen }) => $isOpen ? '1' : '0'};
  /* The text stays in the DOM for crawlers, but a collapsed answer must not be
     reachable by Tab or read out: hide it from focus and assistive technology
     once the collapse transition has finished. */
  visibility: ${({ $isOpen }) => $isOpen ? 'visible' : 'hidden'};
  transition: grid-template-rows 0.3s ease, opacity 0.3s ease,
    visibility 0s linear ${({ $isOpen }) => $isOpen ? '0s' : '0.3s'};

  > div {
    overflow: hidden;
  }

  p {
    padding: ${({ $split }) => ($split ? '0 16px 16px' : '0 var(--spacing-lg) var(--spacing-lg)')};
    color: var(--text-secondary);
    font-size: ${({ $split }) => ($split ? 'var(--fs-sm)' : 'inherit')};
    line-height: ${({ $split }) => ($split ? 'var(--lh-normal)' : 'var(--lh-relaxed)')};
    margin: 0;
  }

  /* Links in body text: the AA-contrast blue (#0A7DAA, about 4.6:1 on white)
     and underlined, so they are not told apart by colour alone. */
  a {
    color: var(--color-accent-primary-fill);
    text-decoration: underline;
    text-underline-offset: 2px;
  }

  ul {
    padding: 0 var(--spacing-lg) var(--spacing-lg) var(--spacing-2xl);
    margin: 0;

    li {
      color: var(--text-secondary);
      margin-bottom: var(--spacing-xs);
    }
  }
`;

const FAQLinks = styled.div<{ $split?: boolean }>`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  padding: ${({ $split }) => ($split ? '0 16px 14px' : '0 var(--spacing-lg) var(--spacing-md)')};
  margin-top: -4px;

  a {
    color: var(--color-accent-primary-fill);
    font-weight: var(--font-weight-semibold);
    font-size: var(--fs-sm);
    text-decoration: underline;
    text-underline-offset: 4px;
    display: inline-flex;
    align-items: center;

    &:hover {
      color: var(--davinci-denim);
    }
  }
`;

export interface FAQItemData {
  question: string;
  /** May carry links. Always rendered into the DOM, collapsed or not, so it stays crawlable. */
  answer: React.ReactNode;
  /** Optional inline conversion links displayed below the answer. */
  links?: Array<{ label: string; to: string }>;
}

export interface FAQProps {
  heading?: string;
  subheading?: React.ReactNode;
  items?: FAQItemData[];
  /** Sit inside a page that already provides the container and background. */
  embedded?: boolean;
  /**
   * `split` (round 27): a left-aligned heading column beside the accordion, one
   * stacked column on phones, with tighter rows. Every answer is still rendered
   * into the DOM exactly as in the stacked layout.
   */
  layout?: 'stacked' | 'split';
  /** Small caps line above the heading (split layout only). */
  eyebrow?: string;
  /** Extra content under the heading in the split layout, e.g. a phone line or a painting. */
  aside?: React.ReactNode;
  /**
   * Split layout only: show this many questions, and fold the rest behind a "More questions"
   * button. The folded questions and answers stay in the HTML (hidden from Tab and screen readers
   * until opened, like a collapsed answer), so nothing is removed from the page.
   */
  initialVisible?: number;
  /** Called when a visitor expands a question (not when one is collapsed). */
  onOpen?: (item: FAQItemData, index: number) => void;
}

const SectionTitle = styled.h2<{ $split?: boolean }>`
  text-align: ${({ $split }) => ($split ? 'left' : 'center')};
  color: ${({ $split }) => ($split ? 'var(--davinci-midnight)' : 'var(--text-primary)')};
  margin-bottom: ${({ $split }) => ($split ? '0' : 'var(--spacing-md)')};
  ${({ $split }) => $split && `
    font-size: var(--fs-xl);
    font-weight: var(--font-weight-extra-bold);
    line-height: var(--lh-snug);
    letter-spacing: -0.01em;

    @media (min-width: 768px) {
      font-size: var(--fs-2xl);
    }
  `}
`;

const SplitGrid = styled.div`
  display: grid;
  gap: 18px;

  @media (min-width: 960px) {
    grid-template-columns: minmax(0, 4fr) minmax(0, 7fr);
    gap: 48px;
    align-items: start;
  }
`;

/* sticky only holds where an ancestor does not clip overflow; ServiceLayout's banded page uses overflow: clip. */
const SplitAside = styled.div`
  @media (min-width: 960px) {
    position: sticky;
    top: 140px;
  }
`;

const MoreRows = styled.div<{ $open: boolean }>`
  display: grid;
  grid-template-rows: ${({ $open }) => ($open ? '1fr' : '0fr')};
  visibility: ${({ $open }) => ($open ? 'visible' : 'hidden')};
  transition: grid-template-rows 0.3s ease,
    visibility 0s linear ${({ $open }) => ($open ? '0s' : '0.3s')};

  > div {
    min-height: 0;
    overflow: hidden;
  }
`;

const MoreButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 10px 4px;
  border: 0;
  background: none;
  color: var(--color-accent-primary-fill);
  font: inherit;
  font-size: var(--fs-sm);
  font-weight: var(--font-weight-bold);
  cursor: pointer;

  svg {
    width: 18px;
    height: 18px;
    transition: transform 0.3s ease;
  }

  &[aria-expanded='true'] svg {
    transform: rotate(180deg);
  }

  &:focus-visible {
    outline: 3px solid var(--davinci-blue-fill);
    outline-offset: 2px;
  }
`;

const SectionSubtitle = styled.p`
  text-align: center;
  color: var(--text-secondary);
  font-size: var(--fs-lg);
  max-width: 600px;
  margin: 0 auto;
`;

export const defaultFaqs: FAQItemData[] = [
  {
    question: "Will AI replace our employees?",
    answer: "No. We give your people tools they direct, rather than replace them. Your accountant reviews what an AI drafts and decides what goes ahead. Your support tech has an AI that sorts and briefs each request while they handle the hard ones. The person stays accountable."
  },
  {
    question: "How long until we see real results?",
    answer: "It depends on the project, so we agree the scope and a first milestone with you before we start. We begin with one narrow process, so you can see something working early. Larger changes come in stages after that."
  },
  {
    question: "Is AI too complex for our team?",
    answer: "It shouldn't be. The AI works behind the scenes while your team uses familiar tools, and we train the people who use it. We're not asking anyone to become a data scientist."
  },
  {
    question: "We tried AI before and it didn't work. Why would this be different?",
    answer: "AI projects often fail because the scope was too big or nobody owned the result. Sometimes we tell you honestly that your vision is too ambitious for current technology, or needs building in steps. We start small, check the result with you, then decide the next step."
  },
  {
    question: "What if the AI makes mistakes?",
    answer: "It will, so we design for it. By default a person checks anything that touches customers, money or records, and we fix the cause, not just the output."
  },
  {
    question: "How do we know our data is safe?",
    answer: "Where your data lives is agreed before we build anything. We can deploy on your servers or in your own cloud account, and we use your security protocols. We're an Alberta company based in Calgary."
  },
  {
    question: "What makes you different from big consulting firms?",
    answer: "We implement, not just recommend. We speak business, not tech jargon. And we're a Calgary team: for IT and onsite work, we can meet in person."
  },
  {
    question: "Are you tied to particular vendors?",
    answer: "No. The agents we run use models from several providers, and we can run two side by side to compare. We pick the model for the job, and can use your own API accounts."
  },
  {
    question: "Can we start small?",
    answer: "That's how we prefer to start. Pick one problem that's driving everyone crazy. We agree the scope, build that, and you decide whether to expand once you've seen it working. No massive commitments.",
    links: [
      { label: 'Take the free AI Readiness Score', to: '/assessment' },
      { label: 'Talk through a starter project', to: '/contact' }
    ]
  },
  {
    question: "What if our industry is 'different'?",
    answer: "Your industry has its own constraints, and a one-size-fits-all approach will miss them. We begin by learning how your business actually runs. Then we apply the functions every sector shares, such as process automation, data analysis and workflow optimization, to your situation."
  },
  {
    question: "What's this really going to cost?",
    answer: "It depends on the scope, so we price after a conversation about the problem. The 30-minute call is free, and discovery is a fixed fee agreed in writing. We then agree scope and price in writing before building starts.",
    links: [
      { label: 'Try the pricing calculator', to: '/pricing' },
      { label: 'Ask for a written quote', to: '/contact' }
    ]
  }
];

const DEFAULT_HEADING = 'Common Concerns, Honest Answers';
const DEFAULT_SUBHEADING = 'No sales speak. Just straight talk about what AI can and can\'t do for your business.';

const FAQ: React.FC<FAQProps> = ({
  heading = DEFAULT_HEADING,
  subheading,
  items = defaultFaqs,
  embedded = false,
  layout = 'stacked',
  eyebrow,
  aside,
  initialVisible,
  onOpen,
}) => {
  const split = layout === 'split';
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [showMore, setShowMore] = useState(false);
  const moreId = `${useId()}-more`;
  const baseId = useId();

  const toggleFAQ = (index: number) => {
    const opening = openIndex !== index;
    setOpenIndex(opening ? index : null);
    if (opening) onOpen?.(items[index], index);
  };

  const intro = subheading ?? (items === defaultFaqs ? DEFAULT_SUBHEADING : null);
  const Wrapper: React.ElementType = embedded ? React.Fragment : 'div';
  const wrapperProps = embedded ? {} : { className: 'container' };

  const rows = items.map((faq, index) => {
    const item = (
      <FAQItem $split={split}>
        <FAQQuestion
          id={`${baseId}-q${index}`}
          onClick={() => toggleFAQ(index)}
          $isOpen={openIndex === index}
          $split={split}
          aria-expanded={openIndex === index}
          aria-controls={`${baseId}-a${index}`}
        >
          <h3>{faq.question}</h3>
          <ChevronDownIcon />
        </FAQQuestion>
        <FAQAnswer
          id={`${baseId}-a${index}`}
          $isOpen={openIndex === index}
          $split={split}
        >
          <div>
            <p>{faq.answer}</p>
            {faq.links && faq.links.length > 0 && (
              <FAQLinks $split={split}>
                {faq.links.map((link) => (
                  <Link key={link.to} to={link.to}>
                    {link.label} &rarr;
                  </Link>
                ))}
              </FAQLinks>
            )}
          </div>
        </FAQAnswer>
      </FAQItem>
    );
    // The split layout is a calm block: no per-row fade, so nothing sits half-faded while it is read.
    return split ? <React.Fragment key={faq.question}>{item}</React.Fragment> : (
      <SimpleFadeIn key={faq.question} delay={index * 50}>{item}</SimpleFadeIn>
    );
  });

  if (split) {
    return (
      <FAQSection $embedded={embedded} $split>
        <Wrapper {...wrapperProps}>
          <SplitGrid>
            <SplitAside>
              {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
              <SectionTitle $split>{heading}</SectionTitle>
              {intro && <SectionSubtitle>{intro}</SectionSubtitle>}
              {aside}
            </SplitAside>
            <FAQContainer $split>
              {split && initialVisible && initialVisible < rows.length ? (
                <>
                  {rows.slice(0, initialVisible)}
                  <MoreRows id={moreId} $open={showMore}>
                    <div>{rows.slice(initialVisible)}</div>
                  </MoreRows>
                  <MoreButton type="button" aria-expanded={showMore} aria-controls={moreId} onClick={() => setShowMore((current) => !current)}>
                    {showMore ? 'Fewer questions' : 'More questions'}
                    <ChevronDownIcon aria-hidden="true" />
                  </MoreButton>
                </>
              ) : rows}
            </FAQContainer>
          </SplitGrid>
        </Wrapper>
      </FAQSection>
    );
  }

  return (
    <FAQSection $embedded={embedded}>
      <Wrapper {...wrapperProps}>
        <SimpleFadeIn>
          <SectionTitle>{heading}</SectionTitle>
          {intro && <SectionSubtitle>{intro}</SectionSubtitle>}
        </SimpleFadeIn>
        
        <FAQContainer>{rows}</FAQContainer>
      </Wrapper>
    </FAQSection>
  );
};

export { defaultFaqs as FAQ_ITEMS };
export default FAQ;